import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Box, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, useTheme } from "@mui/material";
import type { CClassesKey, ILightingClassConfig, IRoadLayoutConfig, ISdFpath, PClassesKey } from "types";
import { roundToTwo } from "utils/helpers";
import { getBorderAreaTargetValues } from "../../utils/border-class";
import { useIlluminanceData } from "../../hooks/useIlluminanceData";
import type { Illuminance, SidewalkStateItem, SurfaceWithIlluminance } from "../../types";
import IlluminanceHeatmap from "../heatmaps/IlluminanceHeatmap";
import CriteriaTable from "./Criteria";
import { RoadState } from "./RoadState";
import getStyles from "./styles";

type IlluminanceTableProps = {
  illuminance: Illuminance;
  surfaces: SurfaceWithIlluminance[];
  sidewalk_placing: string | null;
  subVal: ISdFpath[] | undefined;
  subValResult: SidewalkStateItem[];
  lightingClassConfig: ILightingClassConfig;
  divId?: string;
  ulor: number;
  f_ti?: number;
  s_state: string | null;
  e_state_left: string;
  e_state_right: string;
  roadLayoutConfig: IRoadLayoutConfig;
};

const IlluminanceTable: React.FC<IlluminanceTableProps> = ({
  illuminance,
  surfaces,
  sidewalk_placing,
  subVal,
  subValResult,
  lightingClassConfig,
  divId,
  ulor,
  f_ti,
  s_state,
  e_state_left,
  e_state_right,
  roadLayoutConfig,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const styles = useMemo(() => getStyles(theme).IlluminanceTable, [theme]);

  const {
    ptsX,
    ptsY,
    dimX,
    dimY,
    leftSideWalk,
    rightSideWalk,
  } = useIlluminanceData({ illuminance, surfaces, sidewalk_placing, subVal, subValResult });

  const roadClass = lightingClassConfig.roadClasses;
  const customRoadClass = lightingClassConfig.customClasses?.mainCustomClass;
  const customLeftBorderClass = lightingClassConfig.customClasses?.leftBorderCustomClass;
  const customRightBorderClass = lightingClassConfig.customClasses?.rightBorderCustomClass;
  const borderAreaLeftClass = lightingClassConfig.borderAreaLeftClasses as CClassesKey | PClassesKey;
  const borderAreaRightClass = lightingClassConfig.borderAreaRightClasses as CClassesKey | PClassesKey;

  const roadTarget = getBorderAreaTargetValues(roadClass as CClassesKey | PClassesKey, customRoadClass);
  const leftBorderTarget = getBorderAreaTargetValues(borderAreaLeftClass, customLeftBorderClass);
  const rightBorderTarget = getBorderAreaTargetValues(borderAreaRightClass, customRightBorderClass);

  const ulorDisplay = (ulor || ulor === 0) && !Number.isNaN(Number(ulor)) ? `${Math.round(Number(ulor))}%` : "—";
  const ftiDisplay = f_ti && Number(f_ti) ? roundToTwo(Number(f_ti)) : "—";

  return (
    <Stack sx={styles.container}>
      <Box>
        <Typography variant="h2" sx={styles.title}>
          {t("Illuminance calculation field:")}
          <span id="dimX1">{dimX}</span> m &times; <span id="dimY1">{roundToTwo(dimY)}</span> m (<span id="ptsX1">{ptsX}</span> &times;{" "}
          <span id="ptsY1">{ptsY}</span> {t("pts")} )
        </Typography>

        <TableContainer component={Paper} sx={styles.card}>
          <Table padding="none" sx={styles.table}>
            <TableHead sx={styles.thead}>
              <TableRow>
                <TableCell>{Array.isArray(subVal) && subVal.length > 0 ? t("Road") : " "}</TableCell>
                <TableCell>
                  Ē<sub>m</sub>
                </TableCell>
                <TableCell>
                  E<sub>min</sub>
                </TableCell>
                <TableCell>
                  U<sub>o</sub>
                  {roadClass.includes("P") && <span> *</span>}
                </TableCell>
                <TableCell>
                  U<sub>d</sub>
                  {roadClass.includes("P") && <span> *</span>}
                </TableCell>
                <TableCell>
                  F<sub>ti</sub>
                </TableCell>
                <TableCell>
                  R<sub>UL</sub>
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody sx={styles.tbody}>
              {/* Left Border Area */}
              {Array.isArray(subVal) && subVal.length > 0 && leftSideWalk.name === "sd-fpath-left" && (
                <>
                  <TableRow>
                    <TableCell>{t("Border Area Left")}</TableCell>
                    <TableCell>{leftSideWalk.e_avg.toFixed(2)} lx</TableCell>
                    <TableCell>{leftSideWalk.e_min.toFixed(2)} lx</TableCell>
                    <TableCell>{leftSideWalk.u_oe.toFixed(2)}</TableCell>
                    <TableCell>{leftSideWalk.u_de.toFixed(2)}</TableCell>
                    <TableCell> </TableCell>
                    <TableCell> </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      {t("Target Value")} {customLeftBorderClass?.class_type ? `${customLeftBorderClass.class_type} ` : borderAreaLeftClass}:
                    </TableCell>
                    <TableCell>{leftBorderTarget.eavTargetValue} lx</TableCell>
                    <TableCell>{leftBorderTarget.eminTargetValue !== "" ? `${leftBorderTarget.eminTargetValue} lx` : ""}</TableCell>
                    <TableCell>{leftBorderTarget.uoTargetValue}</TableCell>
                    <TableCell> </TableCell>
                    <TableCell> </TableCell>
                    <TableCell> </TableCell>
                  </TableRow>
                </>
              )}

              {/* Road Row */}
              {illuminance && (
                <>
                  <TableRow>
                    <TableCell>{t("Road")}</TableCell>
                    <TableCell>{roundToTwo(illuminance.e_avg)} lx</TableCell>
                    <TableCell>{roundToTwo(illuminance.e_min)} lx</TableCell>
                    <TableCell>{roundToTwo(illuminance.u_oe)}</TableCell>
                    <TableCell>{roundToTwo(illuminance.u_de)}</TableCell>
                    <TableCell>{ftiDisplay}</TableCell>
                    <TableCell>{ulorDisplay}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      {t("Target Value")} {customRoadClass ? `${customRoadClass.class_type} ` : roadClass}:
                    </TableCell>
                    <TableCell>{roadTarget.eavTargetValue} lx</TableCell>
                    <TableCell>{roadTarget.eminTargetValue !== "" ? `${roadTarget.eminTargetValue} lx` : ""}</TableCell>
                    <TableCell>{roadTarget.uoTargetValue}</TableCell>
                    <TableCell> </TableCell>
                    <TableCell> </TableCell>
                    <TableCell> </TableCell>
                  </TableRow>
                </>
              )}

              {/* Right Border Area */}
              {Array.isArray(subVal) && subVal.length > 0 && rightSideWalk.name === "sd-fpath-right" && (
                <>
                  <TableRow>
                    <TableCell>{t("Border Area Right")}</TableCell>
                    <TableCell>{rightSideWalk.e_avg.toFixed(2)} lx</TableCell>
                    <TableCell>{rightSideWalk.e_min.toFixed(2)} lx</TableCell>
                    <TableCell>{rightSideWalk.u_oe.toFixed(2)}</TableCell>
                    <TableCell>{rightSideWalk.u_de.toFixed(2)}</TableCell>
                    <TableCell></TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      {t("Target Value")} {customRightBorderClass?.class_type ? `${customRightBorderClass.class_type} ` : borderAreaRightClass}:
                    </TableCell>
                    <TableCell>{rightBorderTarget.eavTargetValue} lx</TableCell>
                    <TableCell>{rightBorderTarget.eminTargetValue !== "" ? `${rightBorderTarget.eminTargetValue} lx` : ""}</TableCell>
                    <TableCell>{rightBorderTarget.uoTargetValue}</TableCell>
                    <TableCell></TableCell>
                    <TableCell></TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                </>
              )}
            </TableBody>

            {/* Informational Note */}
            {roadClass.includes("P") && (
              <Typography variant="body2" component="caption" sx={styles.caption}>
                * {t("In M and C classes Uo is normative. Ud is in all classes informative.")}
              </Typography>
            )}
          </Table>

          {/* Met/Not Met Status */}
          <CriteriaTable
            roadLayoutConfig={roadLayoutConfig}
            lightingClassConfig={lightingClassConfig}
            s_state={s_state}
            e_state_left={e_state_left}
            e_state_right={e_state_right}
          />
        </TableContainer>

        {/* Just for printing - Met/Not Met Status */}
        <RoadState
          s_state={s_state ?? ""}
          e_state_left={e_state_left}
          e_state_right={e_state_right}
          roadLayoutConfig={roadLayoutConfig}
          lightingClassConfig={lightingClassConfig}
        />
      </Box>

      {/* Heatmap Section */}
      <IlluminanceHeatmap
        id={divId}
        illuminance={illuminance}
        surfaces={surfaces}
        sidewalkPlacing={sidewalk_placing}
        subVal={subVal}
        subValResult={subValResult}
      />
    </Stack>
  );
};

export default IlluminanceTable;
